import { inject } from '@angular/core'
import { CanActivateFn } from '@angular/router'
import { LogService } from '../services/log.service'
import { AppInitService, AppInitStatus } from '../services/app-init.service'
import { HistoryService } from '../services/history.service'
import { InitDataService } from '../services/init-data.service'
import { KeysService } from '../services/keys.service'
import { CurrencyService } from '../services/currency.service'
import { ConfigService } from '../services/config.service'
import { ConfigVersionService } from '../services/config-version.service'
import { ThemeService } from '../services/theme.service'
import { ClientService } from '../services/client.service'
import { LanguageService } from '../services/language.service'
import { FormAutofillService } from '../services/form-autofill.service'
import { FilterService } from '../services/filter.service'

export const userInitGuard: CanActivateFn = async () => {
  const appInitService = inject(AppInitService)
  const logger = inject(LogService)
  const historyService = inject(HistoryService)
  const languageService = inject(LanguageService)
  const currencyService = inject(CurrencyService)
  const themeService = inject(ThemeService)
  const formAutofillService = inject(FormAutofillService)

  if (appInitService.status() === AppInitStatus.Success) {
    logger.debug('userInitGuard already initialized')
    return Promise.resolve(true)
  }

  appInitService.status.set(AppInitStatus.Loading)

  let continueAfterInit = false
  if (window.parent !== window) {
    await initEmbeddedMode().then((success: boolean) => {
      continueAfterInit = success
    })
  } else {
    await initNormalMode().then((success: boolean) => {
      continueAfterInit = success
    })
  }

  if (!continueAfterInit) {
    return Promise.resolve(true)
  }

  // shared inits
  continueAfterInit = await languageService.initSelectedLanguage()
  if (!continueAfterInit) {
    return Promise.resolve(true)
  }
  currencyService.initSelectedCurrency()
  historyService.init()
  themeService.init()
  formAutofillService.init()
  appInitService.status.set(AppInitStatus.Success)
  return Promise.resolve(true)
}

async function initEmbeddedMode(): Promise<boolean> {
  const logger = inject(LogService)
  const initDataService = inject(InitDataService)
  const keysService = inject(KeysService)
  const languageService = inject(LanguageService)
  const currencyService = inject(CurrencyService)

  logger.debug('Running userInitGuard in embedded mode')
  initDataService.initForEmbeddedUser()
  keysService.loadForEmbeddedUser()
  languageService.initLanguagesForEmbedded()
  currencyService.initCurrenciesForEmbedded()
  return Promise.resolve(true)
}

async function initNormalMode(): Promise<boolean> {
  const logger = inject(LogService)
  const appInitService = inject(AppInitService)
  const initDataService = inject(InitDataService)
  const keysService = inject(KeysService)
  const languageService = inject(LanguageService)
  const currencyService = inject(CurrencyService)
  const configService = inject(ConfigService)
  const clientService = inject(ClientService)
  const versionService = inject(ConfigVersionService)
  const filterService = inject(FilterService)

  logger.debug('Running userInitGuard in normal mode')
  const results = await Promise.allSettled([
    initDataService.initForUser(),
    keysService.loadForUser(),
    currencyService.loadRates(),
  ])
  if (results.some(r => r.status === 'rejected')) {
    logger.fatal('Unable to load required services. ', results)
    appInitService.status.set(AppInitStatus.Error)
    return Promise.resolve(false)
  }
  versionService.tryUpdate(configService.config)
  if (!clientService.client().webshop_is_public) {
    appInitService.status.set(AppInitStatus.NotPublic)
    return Promise.resolve(false)
  }
  languageService.loadLanguages()
  currencyService.loadCurrencies()
  filterService.init()

  return Promise.resolve(true)
}
